// Stale-chunk self-heal.
//
// The app is code-split, so each deploy renames the JS/CSS chunk files (their
// names carry a content hash). A browser tab still holding the previous build —
// or one served a cached index.html — then asks for a chunk filename that no
// longer exists and React throws a ChunkLoadError, which shows up as a blank /
// "Something went wrong" page. We recover by reloading once to pull the fresh
// build; a sessionStorage guard stops it from reload-looping if the real cause
// is something else.

const RELOAD_GUARD_KEY = 'chunkReloadAt';
const RELOAD_MIN_INTERVAL_MS = 15000;

export const isChunkLoadError = (error) => {
    const msg = (error && (error.message || String(error))) || '';
    const name = (error && error.name) || '';
    return (
        name === 'ChunkLoadError' ||
        /Loading chunk [\w-]+ failed/i.test(msg) ||
        /Loading CSS chunk/i.test(msg) ||
        /Failed to fetch dynamically imported module/i.test(msg) ||
        /error loading dynamically imported module/i.test(msg) ||
        /Importing a module script failed/i.test(msg)
    );
};

// Reload once (guarded) when the error is a stale-chunk failure. Returns true if
// it triggered a reload. Safe to call from an ErrorBoundary onError handler.
export const maybeReloadOnChunkError = (error) => {
    if (!isChunkLoadError(error)) { return false; }
    try {
        const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
        // Already reloaded very recently → don't loop; let the error UI show.
        if (Date.now() - last < RELOAD_MIN_INTERVAL_MS) { return false; }
        sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    } catch { /* sessionStorage blocked — still attempt the one reload below */ }
    window.location.reload();
    return true;
};

// Manual "fix a stuck app" action for the System page: drop the service-worker
// caches and any registered service worker, then reload fresh. Does NOT touch
// auth / localStorage, so the admin stays signed in.
export const clearAppCacheAndReload = async () => {
    try {
        if (typeof caches !== 'undefined' && caches.keys) {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k)));
        }
    } catch { /* ignore */ }
    try {
        if (navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
            const regs = await navigator.serviceWorker.getRegistrations();
            // Keep the push service worker (/sw.js) so desktop notifications keep
            // working after a manual cache clear; drop any others.
            await Promise.all(
                regs
                    .filter((r) => !(r.active && r.active.scriptURL && r.active.scriptURL.endsWith('/sw.js')))
                    .map((r) => r.unregister())
            );
        }
    } catch { /* ignore */ }
    // Reset the auto-reload guard so a later real chunk error can still self-heal.
    try { sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch { /* ignore */ }
    window.location.reload();
};
